import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SimplePDFViewer from '../components/SimplePDFViewer';
import { fetchPDF } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || '/cv/api';

function formatTimeLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expiré';
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}j ${h % 24}h`;
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
}

export default function ViewPDF() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewerData, setViewerData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [siteSettings, setSiteSettings] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const welcomeRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setError('Token manquant'); setLoading(false); return; }

    fetch(`${API_URL}/site-settings`).then(r => r.json()).then(d => setSiteSettings(d.settings)).catch(() => {});

    fetchPDF(token)
      .then(data => { setViewerData(data); setLoading(false); welcomeRef.current = setTimeout(() => setShowWelcome(false), 3000); })
      .catch(err => { setError(err.message); setLoading(false); });

    return () => { if (welcomeRef.current) clearTimeout(welcomeRef.current); };
  }, [searchParams]);

  // Raccourcis clavier : ← → pour changer de document
  useEffect(() => {
    if (!viewerData || viewerData.mode !== 'v2') return;
    const docs = viewerData.documents;
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft' && activeTab > 0) { e.preventDefault(); setActiveTab(i => i - 1); }
      else if (e.key === 'ArrowRight' && activeTab < docs.length - 1) { e.preventDefault(); setActiveTab(i => i + 1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [viewerData, activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-xs text-gray-400">Veuillez contacter le webmaster pour obtenir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  // Mode V1
  if (viewerData?.mode === 'v1') {
    return <SimplePDFViewer pdfConfig={viewerData} />;
  }

  // Mode V2 : barre de navigation docs + SimplePDFViewer standard dessous
  if (viewerData?.mode === 'v2') {
    const { set, documents, token, viewer_info } = viewerData;
    if (!documents?.length) {
      return <div className="flex items-center justify-center min-h-screen bg-gray-50"><p className="text-gray-500">Aucun document.</p></div>;
    }

    const doc = documents[activeTab];
    const pdfUrl = `${API_URL}/pdf/document/${doc.id}`;
    const total = documents.length;
    const hasPrev = activeTab > 0;
    const hasNext = activeTab < total - 1;
    const timeLeft = formatTimeLeft(viewer_info?.expires_at);

    return (
      <div className="h-screen flex flex-col">
        {/* Toast bienvenue */}
        <div className={`fixed top-3 left-1/2 -translate-x-1/2 z-50 pointer-events-none transition-all duration-700 ${showWelcome && siteSettings?.welcome_message ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <div className="bg-gray-900/90 backdrop-blur text-white px-5 py-2.5 rounded-full shadow-xl text-sm">
            {siteSettings?.welcome_message || ''}
          </div>
        </div>

        {/* Barre navigation documents — fond noir */}
        <div className="bg-gray-900 flex-shrink-0">
          <div className="h-12 flex items-center px-5">
            {/* Gauche : onglets */}
            <div className="flex gap-1.5">
              {documents.map((d, i) => (
                <button key={d.id} onClick={() => setActiveTab(i)}
                  className={`px-4 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${
                    activeTab === i
                      ? 'bg-white text-gray-900 font-semibold'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}>
                  {d.category_label}
                </button>
              ))}
            </div>

            <div className="flex-1" />

            {/* Droite : stats + déconnexion */}
            <div className="flex items-center gap-4">
              {viewer_info?.view_count !== undefined && (
                <div className="flex items-center gap-1.5 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm tabular-nums">{viewer_info.view_count} vues</span>
                </div>
              )}
              {timeLeft && (
                <div className={`flex items-center gap-1.5 ${timeLeft === 'Expiré' ? 'text-red-400' : 'text-gray-400'}`}
                  title={viewer_info?.expires_at ? new Date(viewer_info.expires_at).toLocaleString('fr-CH') : ''}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm tabular-nums">{timeLeft}</span>
                </div>
              )}
              
              {/* Bouton déconnexion */}
              <button 
                onClick={() => navigate('/thank-you')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
                title="Se déconnecter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Se déconnecter</span>
              </button>
            </div>
          </div>
        </div>

        {/* SimplePDFViewer standard (avec sa propre toolbar page/zoom/download) */}
        <div className="flex-1 overflow-hidden">
          <SimplePDFViewer
            key={doc.id}
            pdfConfig={{
              url: pdfUrl,
              httpHeaders: { 'Authorization': `Bearer ${token}` }
            }}
          />
        </div>

        {/* Footer centré */}
        <div className="bg-white border-t border-gray-200 px-4 py-2 flex-shrink-0">
          <div className="text-center text-xs text-gray-400 space-y-0.5">
            <p>{siteSettings?.footer_copyright || `© ${new Date().getFullYear()} Pixel3000`}</p>
            <p className="italic text-gray-300">{siteSettings?.footer_tagline || 'Partagez vos documents en toute sécurité.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
